import { Activity, memo, useEffect, useMemo, useRef, useState } from 'react';

import { Speaker } from '@/types/speakers.types';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import HoverCard from '@ui/cards/hover-card/hover-card';
import HoverContent from '@ui/cards/hover-card/hover-content';
import PreHoverContent from '@ui/cards/hover-card/pre-hover-content';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import useLanguageParams from '@/utils/use-language-params';
import { useAppSelector } from '@/store/hooks';
import {
  selectGreenroomSessionMeetings,
  selectHomepageAndi18nLanguages,
  selectWorkingProjectDefaultLanguage,
} from '@/store/slices/project/selectors';
import useTrackBoundingClient from '@/utils/use-track-bounding-client';
import getCastInviteStatus from './helpers/use-cast-select-invite-status';
import { getActiveChannel } from '@/store/slices/user/selectors';
import useSortedAndPagedCastList from './helpers/use-sorted-and-paged-cast-list';
import GlobalContainer from '@ui/container/global-container';
import CastActions from './cast-actions';
import EditCastMember from './edit-cast-member';
import { useCastAndCrewContext } from '../../cast-and-crew-context/use-cast-and-crew-context';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import getProjectOverChannelColorCssVariable from '@/utils/colors/get-project-over-channel-color-css-variable';

type TCastCardsProps = {
  speakers: Speaker[];
  sessionUuid: string;
};

const MAX_CAST_CARDS_PER_ROW = 7;
const CAST_CARDS_GAP = 16;

const CastCards = memo(({ speakers: propsSpeakers, sessionUuid }: TCastCardsProps) => {
  const { sortedCast } = useSortedAndPagedCastList({ speakers: propsSpeakers, page: 1 });
  const cast = useMemo(() => sortedCast || [], [sortedCast]);

  const { dispatch: contextDispatch } = useCastAndCrewContext();

  const firstCardRef = useRef<HTMLDivElement | null>(null);

  const activeChannel = useAppSelector(getActiveChannel);

  const meetings = useAppSelector(selectGreenroomSessionMeetings);
  const sessionMeetingData = meetings?.[sessionUuid || ''] || null;

  const languages = useAppSelector(selectHomepageAndi18nLanguages);
  const paramsLang = useLanguageParams();
  const _defaultLang = useAppSelector(selectWorkingProjectDefaultLanguage);
  const defaultLang = _defaultLang || 'en';
  const language = paramsLang || defaultLang;

  const [popoverIsOpen, setPopoverIsOpen] = useState<number | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [editSpeaker, setEditSpeaker] = useState<Speaker | null>(null);

  const castCount = cast.length;

  useEffect(() => {
    if (castCount <= MAX_CAST_CARDS_PER_ROW) {
      setShowMore(false);
    }
  }, [castCount]);

  const firstCardBoundingRect = useTrackBoundingClient({
    ref: firstCardRef,
    open: true,
    trackOnWindowResize: true,
    rerunCounter: castCount,
  });

  const gridMaxHeight = useMemo(() => {
    if (!firstCardBoundingRect) return undefined;
    if (castCount < MAX_CAST_CARDS_PER_ROW) return undefined;

    if (showMore) {
      return '100%';
    }
    return `${firstCardBoundingRect.height + CAST_CARDS_GAP}px`;
  }, [firstCardBoundingRect, showMore, castCount]);

  const isSmallCard = castCount >= 5;

  if (!activeChannel) return null;
  return (
    <div>
      <SlideDownCard
        height={showMore ? undefined : `calc(${firstCardBoundingRect?.height || 0}px`}
        open
        autoGrow={showMore}
        wrapperClassname="overflow-hidden"
      >
        <Container
          className={`grid grid-template-rows-auto gap-${CAST_CARDS_GAP} ${showMore ? 'overflow-auto' : 'overflow-hidden'} no-scrollbar grid-template-columns-${isSmallCard ? 7 : 4}`}
          maxHeight={gridMaxHeight}
        >
          {cast.map((speaker, idx) => {
            const name = `${speaker.first_name || ''} ${speaker.last_name || ''}`.trim();
            const inviteStatus = getCastInviteStatus({ speaker, activeChannel, sessionMeetingData });
            /* commenting out for now, but if we need to go back to right aligning the popover, it's nicer to bring this back in */
            // const isFinalCardInFullRow = (idx + 1) % MAX_CAST_CARDS_PER_ROW === 0;

            return (
              <Container
                key={speaker.speaker}
                aspectRatio={1}
                ref={idx === 0 ? firstCardRef : undefined}
                inert={!showMore && idx > 6}
                className="position-relative cursor-pointer"
                // Entire card is clickable to open edit speaker details modal
                // we don't need this to be accessible by keyboard as there is a separate edit button in the actions
                onClick={() => {
                  setEditSpeaker(speaker);
                }}
              >
                <HoverCard
                  forceHoverState={editSpeaker?.speaker === speaker.speaker || popoverIsOpen === speaker.speaker}
                  backgroundImg={speaker.image}
                >
                  <PreHoverContent>
                    <Container
                      className="width-100-percent height-100-percent flex-column jc-space-between position-relative"
                      gradientOverlay={{
                        height: '20%',
                        /* This is just the color designers used in figma - of course not using their own design system */
                        background: 'linear-gradient(180deg, rgba(26, 26, 26, 0) 0%, rgba(26, 26, 26, 0.50) 100%)',
                      }}
                    >
                      <Activity mode={speaker.image ? 'hidden' : 'visible'}>
                        <Text
                          tag="h3"
                          color="var(--primitives-mode-1-gray-100)"
                          className="H3 position-absolute left-0 right-0 top-0 bottom-0 flex-column ai-center jc-center"
                        >
                          {`${speaker.first_name?.[0]}${speaker.last_name?.[0]}`.trim() || speaker.email?.[0] || ''}
                        </Text>
                      </Activity>

                      <Activity mode={inviteStatus === 'Invite sent' || speaker.hidden ? 'visible' : 'hidden'}>
                        <Container className="margin-l-auto margin-r-8 margin-t-8 flex ai-center gap-4">
                          <Activity mode={speaker?.hidden ? 'visible' : 'hidden'}>
                            <GlobalContainer
                              className="radius-full flex ai-center pc-center square-24"
                              css={{
                                'border-radius': '100px',
                                border: '1px solid var(--global-opacity-white-10)',
                                'background-color': 'var(--local-bttn-sec-transp-pressed-bg)',
                                'backdrop-filter': 'blur(7.5px)',
                              }}
                            >
                              <Icon
                                name={ICONS.VIEWERS_EYE_OFF_SOLID}
                                size={10}
                                color="var(--primitives-mode-1-gray-100)"
                              />
                            </GlobalContainer>
                          </Activity>

                          <Activity mode={inviteStatus === 'Invite sent' ? 'visible' : 'hidden'}>
                            <Container
                              className="radius-full flex ai-center pc-center square-24"
                              background="var(--primitives-mode-1-orange-500)"
                              border="1px solid var(--global-opacity-white-10)"
                            >
                              <Icon name={ICONS.SEND_3} size={10} color="var(--local-bttn-sec-default-icotext)" />
                            </Container>
                          </Activity>
                        </Container>
                      </Activity>

                      <Container className="margin-t-auto margin-b-0 padding-12">
                        <Text clamp={1} className="S1" color="var(--primitives-mode-1-gray-100)">
                          {name}
                        </Text>
                      </Container>
                    </Container>
                  </PreHoverContent>

                  <HoverContent
                    backgroundColor="rgba(var(--primitives-mode-1-gray-800-rgb), 0.75)"
                    backdropFilter="blur(20px)"
                  >
                    <Container className="flex-column" padding="12px" height="100%">
                      <Text
                        wordBreak="break-all"
                        clamp={1}
                        className={`S1 margin-b-${isSmallCard ? '2' : '4'}`}
                        color="var(--primitives-mode-1-gray-100)"
                      >
                        {name}
                      </Text>

                      <Text
                        wordBreak="break-all"
                        clamp={1}
                        className={`C1 margin-b-${isSmallCard ? '4' : CAST_CARDS_GAP}`}
                        color="var(--primitives-mode-1-gray-400)"
                      >
                        {speaker?.job_title?.[language] || speaker.job_title.base || ''}{' '}
                        {speaker?.location ? ` • ${speaker.location}` : ''}
                      </Text>

                      <Text
                        wordBreak="break-all"
                        clamp={isSmallCard ? 1 : 3}
                        className={`C2 margin-b-${isSmallCard ? '4' : CAST_CARDS_GAP}`}
                        color="var(--primitives-mode-1-gray-400)"
                      >
                        {speaker?.description?.[language] || speaker.description.base || ''}
                      </Text>

                      <div className="flex ai-center jc-flex-start gap-8 margin-t-auto margin-b-0 margin-l-auto margin-r-0">
                        {/* View speaker is not MVP */}
                        {/* <Button
                          design="LocalBttnPrim"
                          designSize="small"
                          borderRadius="100px"
                          clear
                        >
                          View
                        </Button> */}
                        <CastActions
                          sessionUuid={sessionUuid}
                          onMenuOpen={() => setPopoverIsOpen(speaker.speaker)}
                          onMenuClose={() => setPopoverIsOpen(null)}
                          speaker={speaker}
                          onEditSpeakerClick={setEditSpeaker}
                          /* commenting out for now, but if we need to go back to right aligning the popover, it's nicer to bring this back in */
                          // leftAlignPopover={isFinalCardInFullRow}
                        />
                      </div>
                    </Container>
                  </HoverContent>
                </HoverCard>
              </Container>
            );
          })}

          {/* add card */}
          {cast.length < 4 ? (
            <Container
              aspectRatio={1}
              design="GlobalCont1"
              border="1px dashed var(--global-cont-4)"
              borderRadius="16px"
            >
              <Button
                design="LocalBttnSecTransp"
                borderRadius="16px"
                width="100%"
                height="100%"
                className="flex ai-center jc-center"
                onClick={() => {
                  contextDispatch({ type: 'TOGGLE_ADD_CAST_AND_CREW_OPEN', payload: true });
                }}
              >
                <Text className="flex ai-center gap-6 S2" color={getProjectOverChannelColorCssVariable()}>
                  <Icon name={ICONS.ADD} size={12} />
                  <span>Add</span>
                </Text>
              </Button>
            </Container>
          ) : null}
        </Container>
      </SlideDownCard>

      {/* show more button */}
      {cast.length > MAX_CAST_CARDS_PER_ROW ? (
        <div className={`flex ai-center jc-center margin-t-${CAST_CARDS_GAP}`}>
          <Button
            design="LocalBttnSecHalfTransp"
            onClick={() => {
              setShowMore((prev) => !prev);
            }}
          >
            <Text className="L2">Show {showMore ? 'Less' : 'More'} </Text>
          </Button>
        </div>
      ) : null}

      <EditCastMember
        editSpeaker={editSpeaker}
        setEditSpeaker={setEditSpeaker}
        defaultLang={defaultLang}
        languages={languages}
      />
    </div>
  );
});

CastCards.displayName = 'CastCards';

export default CastCards;
